import { Banknote, CreditCard, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { formatCurrency } from "@/lib/money";

export type CardPaymentPhase = "idle" | "processing" | "success" | "error";

export interface QuickSaleCardPaymentModalProps {
  open: boolean;
  totalGross: number;
  phase: CardPaymentPhase;
  useTerminal: boolean;
  error: string | null;
  onClose: () => void;
  onRetry: () => void;
  onSwitchToCash: () => void;
  onConfirmManual: () => void;
}

export function QuickSaleCardPaymentModal({
  open,
  totalGross,
  phase,
  useTerminal,
  error,
  onClose,
  onRetry,
  onSwitchToCash,
  onConfirmManual,
}: QuickSaleCardPaymentModalProps) {
  const isProcessing = phase === "processing";
  const isError = phase === "error";
  const isSuccess = phase === "success";

  return (
    <Modal
      open={open}
      onClose={isProcessing ? () => {} : onClose}
      title="Kartenzahlung"
      description={
        useTerminal
          ? "Die Zahlung wird über die ZVT-Schnittstelle an das EC-Terminal übermittelt."
          : "Kartenzahlung ohne Terminal verbuchen."
      }
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          {isError ? (
            <>
              <Button type="button" variant="secondary" onClick={onClose}>
                Abbrechen
              </Button>
              <Button type="button" variant="secondary" onClick={onSwitchToCash}>
                <Banknote className="mr-1.5 h-4 w-4" aria-hidden />
                Barzahlung
              </Button>
              <Button type="button" onClick={onRetry}>
                <RefreshCw className="mr-1.5 h-4 w-4" aria-hidden />
                Erneut versuchen
              </Button>
            </>
          ) : useTerminal ? (
            <Button type="button" variant="secondary" disabled={isProcessing} onClick={onClose}>
              Abbrechen
            </Button>
          ) : (
            <>
              <Button type="button" variant="secondary" disabled={isProcessing} onClick={onClose}>
                Abbrechen
              </Button>
              <Button type="button" loading={isProcessing} onClick={onConfirmManual}>
                Kartenzahlung verbuchen
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        <div className="rounded-lg border border-separator bg-fill-tertiary p-4">
          <p className="text-sm text-label-secondary">Betrag</p>
          <p className="text-2xl font-semibold tabular-nums">
            {formatCurrency(String(totalGross.toFixed(2)))}
          </p>
        </div>

        {useTerminal ? (
          <div className="space-y-3 rounded-lg border border-separator bg-surface p-4">
            <div className="flex items-start gap-3">
              <CreditCard
                className={`mt-0.5 h-5 w-5 shrink-0 ${
                  isProcessing ? "animate-pulse text-accent" : "text-label-secondary"
                }`}
                aria-hidden
              />
              <div className="min-w-0">
                <p className="font-medium text-label">ZVT-Datenaustausch</p>
                {phase === "idle" || phase === "processing" ? (
                  <>
                    <p className="mt-1 text-sm text-label-secondary">
                      Verbindung zum CampingDeviceService wird hergestellt…
                    </p>
                    <p className="mt-1 text-sm text-label-secondary">
                      Bitte den Gast am EC-Terminal bezahlen lassen.
                    </p>
                    {isProcessing ? (
                      <p className="mt-2 text-sm font-medium text-accent">
                        Warte auf Terminal-Antwort…
                      </p>
                    ) : null}
                  </>
                ) : isSuccess ? (
                  <p className="mt-1 text-sm text-status-green">
                    Kartenzahlung am Terminal erfolgreich.
                  </p>
                ) : isError ? (
                  <p className="mt-1 text-sm text-status-red">
                    {error ?? "Die Kartenzahlung am Terminal ist fehlgeschlagen."}
                  </p>
                ) : null}
              </div>
            </div>

            <ul className="space-y-1 border-t border-separator pt-3 text-xs text-label-secondary">
              <li>1. Zahlungsbetrag wird an das Terminal gesendet</li>
              <li>2. Gast zahlt mit Karte am EC-Terminal</li>
              <li>3. Terminal bestätigt Zahlung an CampDesk</li>
              <li>4. Verkauf wird nach erfolgreicher Bestätigung verbucht</li>
            </ul>
          </div>
        ) : (
          <p className="text-sm text-label-secondary">
            EC-Terminal ist nicht konfiguriert. Die Kartenzahlung wird manuell verbucht (ohne
            Terminal-Kommunikation).
          </p>
        )}

        {!useTerminal && error ? <p className="text-sm text-status-red">{error}</p> : null}
      </div>
    </Modal>
  );
}
